'use client';

import React, { useState, useTransition } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { signIn } from 'next-auth/react';
import Script from 'next/script';
import styles from './login.module.scss';
import { useTranslation } from '@/hooks/useTranslation';
import OtpModal from '@/components/OtpModal';



export default function LoginForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const rawCallback = searchParams.get('callbackUrl') ?? '/dashboard';
  // Never let a social login land on an admin page - if the callbackUrl is
  // /autonomo-admin (set by proxy when unauthenticated user visited admin),
  // reset it to /dashboard so role-based redirect happens naturally.
  const callbackUrl = rawCallback.startsWith('/autonomo-admin') ? '/dashboard' : rawCallback;

  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isPending, startTransition] = useTransition();
  // OTP gate: set when login blocked due to unverified email
  const [otpGate, setOtpGate] = useState<{ userId: string; email: string } | null>(null);

  // Detect social-not-registered redirect from auth.ts signIn callback
  const urlError = searchParams.get('error') ?? '';
  const socialEmail = searchParams.get('email') ?? '';
  const socialName = searchParams.get('social') ?? '';
  const isSocialBlocked = urlError === 'SocialNotRegistered';

  const AUTH_ERROR_MAP: Record<string, string> = {
    'ACCOUNT_NOT_FOUND': t('auth.errors.accountNotFound'),
    'ACCOUNT_LOCKED': t('auth.errors.accountLocked'),
    'INVALID_CREDENTIALS': t('auth.errors.invalidCredentials'),
    'ACCOUNT_INACTIVE': t('auth.errors.accountInactive'),
    'GENERIC_ERROR': t('auth.errors.genericError'),
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    startTransition(async () => {
      try {
        // Execute reCAPTCHA v3 token silently
        let recaptchaToken: string | undefined;
        const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
        if (siteKey && typeof window !== 'undefined' && (window as Window & { grecaptcha?: { execute: (k: string, opts: { action: string }) => Promise<string> } }).grecaptcha) {
          try {
            recaptchaToken = await (window as Window & { grecaptcha?: { execute: (k: string, opts: { action: string }) => Promise<string> } }).grecaptcha!.execute(siteKey, { action: 'login' });
          } catch {
            // Non-fatal - proceed without token
          }
        }

        // Step 1: Pre-flight check - validate credentials and get isActive status
        // This lets us handle inactive accounts (OTP gate) WITHOUT relying on
        // NextAuth's error channel, which masks custom errors as CredentialsSignin.
        const checkRes = await fetch('/api/v1/auth/login-check', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim().toLowerCase(), password, recaptchaToken }),
        });
        const checkJson = await checkRes.json();

        if (!checkRes.ok) {
          const code = checkJson.code as string;
          if (code === 'ACCOUNT_LOCKED' && checkJson.data?.minutesLeft) {
            const mins = checkJson.data.minutesLeft as number;
            if (mins > 60 * 24) {
              // Far-future date (admin ban with wrong date) - show generic message
              setError(t('auth.errors.accountLocked'));
            } else if (mins > 60) {
              const hours = Math.ceil(mins / 60);
              setError(t('auth.errors.accountLockedHours', `Conta bloqueada. Tente novamente em ${hours} hora${hours === 1 ? '' : 's'}.`).replace('{{hours}}', String(hours)));
            } else {
              setError(t('auth.errors.accountLockedMinutes', `Conta bloqueada. Tente novamente em ${mins} minuto${mins === 1 ? '' : 's'}.`).replace('{{minutes}}', String(mins)));
            }
          } else {
            setError(AUTH_ERROR_MAP[code] || t('auth.errors.invalidCredentials'));
          }
          return;
        }

        // Account valid but not yet verified - show OTP modal
        if (!checkJson.data.active) {
          setOtpGate({ userId: checkJson.data.userId, email: checkJson.data.email });
          return;
        }

        // Step 2: Account is active - proceed with NextAuth signIn
        const result = await signIn('credentials', {
          email,
          password,
          redirect: false,
        });

        if (result?.error) {
          setError(AUTH_ERROR_MAP[result.error] || t('auth.errors.invalidCredentials'));
          return;
        }

        router.push(callbackUrl);
        router.refresh();
      } catch {
        setError(t('errors.network.serverError'));
      }
    });
  };

  // Social logins always go to /dashboard - the authenticated layout
  // redirects ADMIN roles to /autonomo-admin automatically.
  const handleSocialLogin = (provider: 'google' | 'linkedin' | 'apple') => {
    signIn(provider, { callbackUrl: '/dashboard' });
  };

  return (
    <div className={styles.container}>
      {/* reCAPTCHA v3 - silent, no widget */}
      {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY && (
        <Script
          src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
          strategy="afterInteractive"
        />
      )}
      {/* OTP verification gate - shown when user tries to log in with unverified email */}
      {otpGate && (
        <OtpModal
          userId={otpGate.userId}
          email={otpGate.email}
          onSuccess={() => {
            setOtpGate(null);
            router.push(callbackUrl);
            router.refresh();
          }}
          onClose={() => setOtpGate(null)}
        />
      )}
      <div className={styles.card}>
        {/* Logo */}
        <div className={styles.logo}>
          <Link href="/">
            <img src="/logo.png" alt="Autonomo" className={styles.logoImg} />
          </Link>
        </div>

        <h1 className={styles.title}>{t('auth.login.title')}</h1>
        <p className={styles.subtitle}>{t('auth.login.subtitle')}</p>


        {/* Social-not-registered banner */}
        {isSocialBlocked && (
          <div className="mx-6 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm">
            <p className="font-semibold text-amber-800 mb-1">
              ⚠️ Nenhuma conta encontrada para este endereço {socialName}.
            </p>
            <p className="text-amber-700">
              Por favor{' '}
              <Link href={`/register/seeker${socialEmail ? `?email=${socialEmail}` : ''}`} className="font-bold underline hover:text-amber-900">
                registe-se primeiro
              </Link>
              {' '}- depois pode associar a sua conta {socialName} nas Definições.
            </p>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className={styles.errorBanner} role="alert">
            <span>{error}</span>
          </div>
        )}

        {/* Form */}
        <form className={styles.form} onSubmit={handleLogin} noValidate>
          <div className={styles.fieldGroup}>
            <label htmlFor="email" className={styles.label}>{t('auth.login.emailLabel')}</label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              placeholder={t('auth.login.emailPlaceholder')}
            />
          </div>

          <div className={styles.fieldGroup}>
            <label htmlFor="password" className={styles.label}>{t('auth.login.passwordLabel')}</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                placeholder="••••••••"
                style={{ paddingRight: '44px' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                style={{ position: 'absolute', top: '50%', right: '12px', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '4px' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword
                  ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                  : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                }
              </button>
            </div>
          </div>

          <div className={styles.row}>
            <label className={styles.checkboxLabel}>
              <input type="checkbox" className={styles.checkbox} />
              {t('auth.login.rememberMe')}
            </label>
            <Link
              href="/forgot-password"
              className={`${styles.forgotLink} ${styles.linkTeal}`}
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          <button
            type="submit"
            id="btn-login"
            disabled={isPending}
            className={`${styles.submitBtn} ${styles.btnTeal}`}
          >
            <span className={styles.spinner} aria-hidden="true" data-visible={isPending} />
            {isPending ? t('common.loading') : t('auth.login.submitButton')}
          </button>
        </form>

        {/* Social login */}
        <div className={styles.divider}>
          <span className={styles.dividerText}>{t('auth.login.orContinueWith')}</span>
        </div>

        <div className={styles.socialButtons}>
          {/* Apple Sign In - only shown when Apple OAuth is configured */}
          {process.env.NEXT_PUBLIC_APPLE_ENABLED === '1' && (
            <button
              type="button"
              id="btn-apple"
              onClick={() => handleSocialLogin('apple')}
              className={styles.socialBtn}
            >
              <svg className={styles.socialIcon} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
              </svg>
              {t('auth.login.appleButton')}
            </button>
          )}
          <button
            type="button"
            id="btn-google"
            onClick={() => handleSocialLogin('google')}
            className={styles.socialBtn}
          >
            <svg className={styles.socialIcon} viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t('auth.login.googleButton')}
          </button>
          <button
            type="button"
            id="btn-linkedin"
            onClick={() => handleSocialLogin('linkedin')}
            className={styles.socialBtn}
          >
            <svg className={styles.socialIcon} viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
            {t('auth.login.linkedinButton')}
          </button>
        </div>

        <p className={styles.registerPrompt}>
          {t('auth.login.noAccount')}{' '}
          <Link
            href="/register"
            className={`${styles.registerLink} ${styles.linkTeal}`}
          >
            {t('auth.login.registerLink')}
          </Link>
        </p>
      </div>
    </div>
  );
}
